/**
 * BudgetItemsView Component
 *
 * Tabela de itens do orçamento com edição inline, cálculos automáticos e ações em lote
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Plus,
  Edit2,
  Save,
  X,
  Trash2,
  MoreVertical,
  Copy,
  Calculator,
  Package,
  Percent,
  DollarSign,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Eye,
  EyeOff
} from 'lucide-react';

interface BudgetItem {
  id: string;
  code?: string;
  description: string;
  details?: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  discount: number;
  discountType: 'percentage' | 'fixed';
  total: number;
  category?: string;
  taxable: boolean;
  notes?: string;
  order?: number;
}

interface BudgetItemsViewProps {
  items: BudgetItem[];
  onItemsChange?: (items: BudgetItem[]) => void;
  currency?: string;
  taxRate?: number;
  editable?: boolean;
  showDetails?: boolean;
  showTaxes?: boolean;
  allowBatchActions?: boolean;
  categories?: string[];
  units?: string[];
  className?: string;
}

const DEFAULT_UNITS = [
  'UN', 'H', 'M²', 'M³', 'KG', 'L', 'CX', 'DZ', 'PAR', 'M', 'TON', 'SC'
];

const DEFAULT_CATEGORIES = [
  'Serviços', 'Produtos', 'Materiais', 'Equipamentos', 'Consultoria',
  'Desenvolvimento', 'Design', 'Marketing', 'Treinamento', 'Suporte'
];

export function BudgetItemsView({
  items,
  onItemsChange,
  currency = 'BRL',
  taxRate = 0.12,
  editable = true,
  showDetails = false,
  showTaxes = true,
  allowBatchActions = true,
  categories = DEFAULT_CATEGORIES,
  units = DEFAULT_UNITS,
  className
}: BudgetItemsViewProps) {
  const [editingItems, setEditingItems] = useState<Set<string>>(new Set());
  const [editingData, setEditingData] = useState<Record<string, Partial<BudgetItem>>>({});
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [newItemDialogOpen, setNewItemDialogOpen] = useState(false);
  const [newItem, setNewItem] = useState<Partial<BudgetItem>>({
    description: '',
    quantity: 1,
    unit: 'UN',
    unitPrice: 0,
    discount: 0,
    discountType: 'percentage',
    taxable: true,
    details: '',
    notes: '',
    category: 'Serviços'
  });

  // Calculate totals whenever items change
  const calculateItemTotal = (item: BudgetItem) => {
    const subtotal = item.unitPrice * item.quantity;
    const discountAmount = item.discountType === 'percentage'
      ? subtotal * (item.discount / 100)
      : item.discount;
    return Math.max(0, subtotal - discountAmount);
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
    const totalDiscount = items.reduce((sum, item) => {
      const itemSubtotal = item.unitPrice * item.quantity;
      const discountAmount = item.discountType === 'percentage'
        ? itemSubtotal * (item.discount / 100)
        : item.discount;
      return sum + discountAmount;
    }, 0);
    const itemsTotal = items.reduce((sum, item) => sum + item.total, 0);
    const taxableTotal = items.filter(item => item.taxable).reduce((sum, item) => sum + item.total, 0);
    const taxes = taxableTotal * taxRate;
    const finalTotal = itemsTotal + taxes;

    return {
      subtotal,
      totalDiscount,
      itemsTotal,
      taxableTotal,
      taxes,
      finalTotal
    };
  };

  const totals = calculateTotals();

  const handleEditItem = (itemId: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    setEditingItems(new Set([...editingItems, itemId]));
    setEditingData({
      ...editingData,
      [itemId]: { ...item }
    });
  };

  const handleSaveItem = (itemId: string) => {
    const editedItem = editingData[itemId];
    if (!editedItem) return;

    const updatedItem = {
      ...editedItem,
      total: calculateItemTotal(editedItem as BudgetItem)
    } as BudgetItem;

    const updatedItems = items.map(item =>
      item.id === itemId ? updatedItem : item
    );

    onItemsChange?.(updatedItems);
    setEditingItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
    delete editingData[itemId];
  };

  const handleCancelEdit = (itemId: string) => {
    setEditingItems(prev => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
    delete editingData[itemId];
  };

  const handleDeleteItem = (itemId: string) => {
    const updatedItems = items.filter(item => item.id !== itemId);
    onItemsChange?.(updatedItems);
  };

  const handleDuplicateItem = (itemId: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    const duplicatedItem = {
      ...item,
      id: `${Date.now()}`,
      description: `${item.description} (cópia)`,
      code: item.code ? `${item.code}-COPY` : undefined
    };

    onItemsChange?.([...items, duplicatedItem]);
  };

  const handleAddNewItem = () => {
    if (!newItem.description || newItem.unitPrice <= 0) return;

    const item: BudgetItem = {
      id: `${Date.now()}`,
      code: newItem.code,
      description: newItem.description!,
      details: newItem.details || '',
      quantity: newItem.quantity || 1,
      unit: newItem.unit || 'UN',
      unitPrice: newItem.unitPrice || 0,
      discount: newItem.discount || 0,
      discountType: newItem.discountType || 'percentage',
      taxable: newItem.taxable !== false,
      notes: newItem.notes || '',
      category: newItem.category || 'Serviços',
      total: calculateItemTotal({
        id: '',
        description: newItem.description!,
        quantity: newItem.quantity || 1,
        unit: newItem.unit || 'UN',
        unitPrice: newItem.unitPrice || 0,
        discount: newItem.discount || 0,
        discountType: newItem.discountType || 'percentage',
        taxable: newItem.taxable !== false
      } as BudgetItem)
    };

    onItemsChange?.([...items, item]);
    setNewItemDialogOpen(false);
    setNewItem({
      description: '',
      quantity: 1,
      unit: 'UN',
      unitPrice: 0,
      discount: 0,
      discountType: 'percentage',
      taxable: true,
      details: '',
      notes: '',
      category: 'Serviços'
    });
  };

  const handleBatchAction = (action: 'delete' | 'discount' | 'tax') => {
    if (selectedItems.size === 0) return;

    let updatedItems = [...items];

    switch (action) {
      case 'delete':
        updatedItems = items.filter(item => !selectedItems.has(item.id));
        break;
      case 'discount':
        // Add 10% discount to selected items
        updatedItems = items.map(item => {
          if (selectedItems.has(item.id)) {
            return {
              ...item,
              discount: item.discount + 10,
              total: calculateItemTotal({ ...item, discount: item.discount + 10 })
            };
          }
          return item;
        });
        break;
      case 'tax':
        // Toggle tax for selected items
        updatedItems = items.map(item => {
          if (selectedItems.has(item.id)) {
            return {
              ...item,
              taxable: !item.taxable
            };
          }
          return item;
        });
        break;
    }

    onItemsChange?.(updatedItems);
    setSelectedItems(new Set());
    setBatchDialogOpen(false);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedItems(new Set(items.map(item => item.id)));
    } else {
      setSelectedItems(new Set());
    }
  };

  const handleSelectItem = (itemId: string, checked: boolean) => {
    const newSelected = new Set(selectedItems);
    if (checked) {
      newSelected.add(itemId);
    } else {
      newSelected.delete(itemId);
    }
    setSelectedItems(newSelected);
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency
    });
  };

  return (
    <div className={`space-y-6 ${className}`}>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Itens do Orçamento
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {items.length} item(s) • Total: {formatCurrency(totals.finalTotal)}
              </p>
            </div>

            {editable && (
              <div className="flex gap-2">
                {allowBatchActions && selectedItems.size > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setBatchDialogOpen(true)}
                  >
                    <Calculator className="h-4 w-4 mr-1" />
                    Ações em Lote ({selectedItems.size})
                  </Button>
                )}
                <Button size="sm" onClick={() => setNewItemDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Adicionar Item
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Nenhum item adicionado</h3>
              <p className="text-muted-foreground mb-4">
                Adicione serviços ou produtos para compor seu orçamento
              </p>
              {editable && (
                <Button onClick={() => setNewItemDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Primeiro Item
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {allowBatchActions && (
                        <TableHead className="w-12">
                          <input
                            type="checkbox"
                            checked={selectedItems.size === items.length}
                            onChange={(e) => handleSelectAll(e.target.checked)}
                            className="rounded"
                          />
                        </TableHead>
                      )}
                      <TableHead className="w-20">Código</TableHead>
                      <TableHead>Descrição</TableHead>
                      {showDetails && <TableHead>Detalhes</TableHead>}
                      <TableHead className="w-24 text-right">Qtd</TableHead>
                      <TableHead className="w-20 text-center">Un</TableHead>
                      <TableHead className="w-32 text-right">Valor Unit.</TableHead>
                      <TableHead className="w-28 text-right">Desconto</TableHead>
                      {showTaxes && (
                        <TableHead className="w-20 text-center">
                          <Tooltip title="Isento de impostos">
                            <span>Tax</span>
                          </Tooltip>
                        </TableHead>
                      )}
                      <TableHead className="w-32 text-right">Total</TableHead>
                      {editable && <TableHead className="w-16"></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {items.map((item, index) => {
                      const isEditing = editingItems.has(item.id);
                      const editingItem = editingData[item.id] || item;

                      return (
                        <TableRow key={item.id} className={selectedItems.has(item.id) ? 'bg-muted/50' : ''}>
                          {allowBatchActions && (
                            <TableCell>
                              <input
                                type="checkbox"
                                checked={selectedItems.has(item.id)}
                                onChange={(e) => handleSelectItem(item.id, e.target.checked)}
                                className="rounded"
                              />
                            </TableCell>
                          )}

                          <TableCell className="font-mono text-xs">
                            {isEditing ? (
                              <Input
                                value={editingItem.code || ''}
                                onChange={(e) => setEditingData({
                                  ...editingData,
                                  [item.id]: { ...editingItem, code: e.target.value }
                                })}
                                placeholder="Código"
                                className="h-8"
                              />
                            ) : (
                              <span className="text-muted-foreground">
                                {item.code || `${String(index + 1).padStart(3, '0')}`}
                              </span>
                            )}
                          </TableCell>

                          <TableCell>
                            {isEditing ? (
                              <div className="space-y-2">
                                <Input
                                  value={editingItem.description || ''}
                                  onChange={(e) => setEditingData({
                                    ...editingData,
                                    [item.id]: { ...editingItem, description: e.target.value }
                                  })}
                                  placeholder="Descrição do item"
                                  className="h-8"
                                />
                                <Select
                                  value={editingItem.category || ''}
                                  onValueChange={(value) => setEditingData({
                                    ...editingData,
                                    [item.id]: { ...editingItem, category: value }
                                  })}
                                >
                                  <SelectTrigger className="h-8">
                                    <SelectValue placeholder="Categoria" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {categories.map((category) => (
                                      <SelectItem key={category} value={category}>
                                        {category}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            ) : (
                              <div>
                                <div className="font-medium">{item.description}</div>
                                {item.category && (
                                  <Badge variant="outline" className="text-xs mt-1">
                                    {item.category}
                                  </Badge>
                                )}
                              </div>
                            )}
                          </TableCell>

                          {showDetails && (
                            <TableCell>
                              {isEditing ? (
                                <Textarea
                                  value={editingItem.details || ''}
                                  onChange={(e) => setEditingData({
                                    ...editingData,
                                    [item.id]: { ...editingItem, details: e.target.value }
                                  })}
                                  placeholder="Detalhes adicionais"
                                  className="min-h-[60px] resize-none"
                                  rows={2}
                                />
                              ) : (
                                <span className="text-sm text-muted-foreground">
                                  {item.details || '-'}
                                </span>
                              )}
                            </TableCell>
                          )}

                          <TableCell className="text-right">
                            {isEditing ? (
                              <Input
                                type="number"
                                value={editingItem.quantity || 1}
                                onChange={(e) => setEditingData({
                                  ...editingData,
                                  [item.id]: {
                                    ...editingItem,
                                    quantity: parseInt(e.target.value) || 1
                                  }
                                })}
                                className="h-8 text-right"
                                min="1"
                              />
                            ) : (
                              <span>{item.quantity}</span>
                            )}
                          </TableCell>

                          <TableCell className="text-center">
                            {isEditing ? (
                              <Select
                                value={editingItem.unit || 'UN'}
                                onValueChange={(value) => setEditingData({
                                  ...editingData,
                                  [item.id]: { ...editingItem, unit: value }
                                })}
                              >
                                <SelectTrigger className="h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {units.map((unit) => (
                                    <SelectItem key={unit} value={unit}>
                                      {unit}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <span className="text-sm font-mono">{item.unit}</span>
                            )}
                          </TableCell>

                          <TableCell className="text-right">
                            {isEditing ? (
                              <Input
                                type="number"
                                value={editingItem.unitPrice || 0}
                                onChange={(e) => setEditingData({
                                  ...editingData,
                                  [item.id]: {
                                    ...editingItem,
                                    unitPrice: parseFloat(e.target.value) || 0
                                  }
                                })}
                                className="h-8 text-right"
                                min="0"
                                step="0.01"
                              />
                            ) : (
                              <span>{formatCurrency(item.unitPrice)}</span>
                            )}
                          </TableCell>

                          <TableCell className="text-right">
                            {isEditing ? (
                              <div className="flex items-center gap-1">
                                <Select
                                  value={editingItem.discountType || 'percentage'}
                                  onValueChange={(value: 'percentage' | 'fixed') => setEditingData({
                                    ...editingData,
                                    [item.id]: { ...editingItem, discountType: value }
                                  })}
                                >
                                  <SelectTrigger className="h-8 w-16">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="percentage">%</SelectItem>
                                    <SelectItem value="fixed">R$</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Input
                                  type="number"
                                  value={editingItem.discount || 0}
                                  onChange={(e) => setEditingData({
                                    ...editingData,
                                    [item.id]: {
                                      ...editingItem,
                                      discount: parseFloat(e.target.value) || 0
                                    }
                                  })}
                                  className="h-8 text-right w-20"
                                  min="0"
                                  step="0.01"
                                />
                              </div>
                            ) : (
                              <div className="text-right">
                                {item.discount > 0 ? (
                                  <Badge variant="secondary">
                                    {item.discountType === 'percentage'
                                      ? `${item.discount}%`
                                      : formatCurrency(item.discount)
                                    }
                                  </Badge>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </div>
                            )}
                          </TableCell>

                          {showTaxes && (
                            <TableCell className="text-center">
                              {isEditing ? (
                                <input
                                  type="checkbox"
                                  checked={editingItem.taxable !== false}
                                  onChange={(e) => setEditingData({
                                    ...editingData,
                                    [item.id]: { ...editingItem, taxable: e.target.checked }
                                  })}
                                  className="rounded"
                                />
                              ) : (
                                <span className={item.taxable ? 'text-green-600' : 'text-muted-foreground'}>
                                  {item.taxable ? (
                                    <CheckCircle2 className="h-4 w-4 mx-auto" />
                                  ) : (
                                    <AlertCircle className="h-4 w-4 mx-auto" />
                                  )}
                                </span>
                              )}
                            </TableCell>
                          )}

                          <TableCell className="text-right font-medium">
                            {formatCurrency(item.total)}
                          </TableCell>

                          {editable && (
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {isEditing ? (
                                    <>
                                      <DropdownMenuItem onClick={() => handleSaveItem(item.id)}>
                                        <Save className="h-4 w-4 mr-2" />
                                        Salvar
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleCancelEdit(item.id)}>
                                        <X className="h-4 w-4 mr-2" />
                                        Cancelar
                                      </DropdownMenuItem>
                                    </>
                                  ) : (
                                    <>
                                      <DropdownMenuItem onClick={() => handleEditItem(item.id)}>
                                        <Edit2 className="h-4 w-4 mr-2" />
                                        Editar
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleDuplicateItem(item.id)}>
                                        <Copy className="h-4 w-4 mr-2" />
                                        Duplicar
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        onClick={() => handleDeleteItem(item.id)}
                                        className="text-red-600"
                                      >
                                        <Trash2 className="h-4 w-4 mr-2" />
                                        Excluir
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Summary */}
              <div className="mt-6 space-y-3">
                <Separator />
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(totals.subtotal)}</span>
                  </div>
                  {totals.totalDiscount > 0 && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>Descontos:</span>
                      <span>-{formatCurrency(totals.totalDiscount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span>Total de Itens:</span>
                    <span>{formatCurrency(totals.itemsTotal)}</span>
                  </div>
                  {showTaxes && totals.taxes > 0 && (
                    <div className="flex justify-between text-sm">
                      <span>Impostos ({(taxRate * 100).toFixed(0)}%):</span>
                      <span>{formatCurrency(totals.taxes)}</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total Final:</span>
                    <span className="text-primary">
                      {formatCurrency(totals.finalTotal)}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Item Dialog */}
      <Dialog open={newItemDialogOpen} onOpenChange={setNewItemDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Adicionar Novo Item</DialogTitle>
            <DialogDescription>
              Preencha as informações do novo item para o orçamento
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Código (opcional)</Label>
                <Input
                  value={newItem.code || ''}
                  onChange={(e) => setNewItem({ ...newItem, code: e.target.value })}
                  placeholder="Ex: SERV001"
                />
              </div>
              <div>
                <Label>Categoria</Label>
                <Select
                  value={newItem.category || 'Serviços'}
                  onValueChange={(value) => setNewItem({ ...newItem, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Descrição *</Label>
              <Input
                value={newItem.description || ''}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                placeholder="Descrição detalhada do item"
              />
            </div>

            <div>
              <Label>Detalhes (opcional)</Label>
              <Textarea
                value={newItem.details || ''}
                onChange={(e) => setNewItem({ ...newItem, details: e.target.value })}
                placeholder="Informações complementares sobre o item"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>Quantidade</Label>
                <Input
                  type="number"
                  value={newItem.quantity || 1}
                  onChange={(e) => setNewItem({ ...newItem, quantity: parseInt(e.target.value) || 1 })}
                  min="1"
                />
              </div>
              <div>
                <Label>Unidade</Label>
                <Select
                  value={newItem.unit || 'UN'}
                  onValueChange={(value) => setNewItem({ ...newItem, unit: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map((unit) => (
                      <SelectItem key={unit} value={unit}>
                        {unit}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valor Unitário *</Label>
                <Input
                  type="number"
                  value={newItem.unitPrice || 0}
                  onChange={(e) => setNewItem({ ...newItem, unitPrice: parseFloat(e.target.value) || 0 })}
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <Label>Desconto</Label>
                <div className="flex items-center gap-1">
                  <Select
                    value={newItem.discountType || 'percentage'}
                    onValueChange={(value: 'percentage' | 'fixed') => setNewItem({ ...newItem, discountType: value })}
                  >
                    <SelectTrigger className="w-16">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">%</SelectItem>
                      <SelectItem value="fixed">R$</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    value={newItem.discount || 0}
                    onChange={(e) => setNewItem({ ...newItem, discount: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>

            {newItem.description && newItem.unitPrice > 0 && (
              <div className="bg-muted/30 rounded-lg p-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Total do Item:</span>
                  <span className="text-lg font-bold text-primary">
                    {formatCurrency(calculateItemTotal({
                      id: '',
                      description: newItem.description,
                      quantity: newItem.quantity || 1,
                      unit: newItem.unit || 'UN',
                      unitPrice: newItem.unitPrice || 0,
                      discount: newItem.discount || 0,
                      discountType: newItem.discountType || 'percentage',
                      taxable: newItem.taxable !== false
                    } as BudgetItem))}
                  </span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setNewItemDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleAddNewItem}
              disabled={!newItem.description || (newItem.unitPrice || 0) <= 0}
            >
              Adicionar Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Batch Actions Dialog */}
      <Dialog open={batchDialogOpen} onOpenChange={setBatchDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ações em Lote</DialogTitle>
            <DialogDescription>
              {selectedItems.size} item(s) selecionados
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => handleBatchAction('discount')}
            >
              <Percent className="h-4 w-4 mr-2" />
              Adicionar 10% de Desconto
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start"
              onClick={() => handleBatchAction('tax')}
            >
              <DollarSign className="h-4 w-4 mr-2" />
              Alternar Tributação
            </Button>
            <Button
              variant="destructive"
              className="w-full justify-start"
              onClick={() => handleBatchAction('delete')}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Excluir Itens Selecionados
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBatchDialogOpen(false)}>
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Helper component for tooltips (simplified version)
const Tooltip: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
  return (
    <div className="group relative inline-block">
      {children}
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-foreground rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
        {title}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-foreground"></div>
      </div>
    </div>
  );
};